import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, Grid, Card, CardContent, TextField, Snackbar, Alert } from '@mui/material';
import { API_BASE } from '../config';

const DETAILS = {
  'healthcare': {
    title: 'Healthcare',
    lead: 'AI-driven diagnostics, patient insights and clinical decision support to improve outcomes.',
    body: 'We build solutions for intelligent triage, medical image analysis, patient segmentation, and predictive risk scoring. Our healthcare projects focus on security, privacy, and regulatory compliance.'
  },
  'finance': {
    title: 'Finance',
    lead: 'Risk modelling, fraud detection and intelligent automation for finance teams.',
    body: 'From anomaly detection in transactions to automated reporting and forecasting, we help finance teams reduce risk and operate more efficiently with explainable ML.'
  },
  'retail': {
    title: 'Retail',
    lead: 'Customer analytics, personalization and real-time recommendations to grow revenue.',
    body: 'Personalization engines, demand forecasting, inventory optimization and in-store analytics help retailers increase conversion and reduce waste.'
  },
  'manufacturing': {
    title: 'Manufacturing',
    lead: 'Predictive maintenance, quality inspection and process optimisation at scale.',
    body: 'We implement sensor analytics, anomaly detection, and optimization pipelines to reduce downtime and improve yield.'
  },
  'local-bar-associations': {
    title: 'Local Bar Associations',
    lead: 'Tools for legal community engagement, knowledge management and compliance.',
    body: 'We deliver portals for case management, knowledge search using semantic search, member collaboration, and compliance tracking tailored to legal organizations.'
  },
  'digital-marketing': {
    title: 'Digital Marketing',
    lead: 'Performance marketing, analytics and attribution to maximise ROI.',
    body: 'Analytics dashboards, campaign attribution models, and automation to scale targeted outreach while optimizing spend.'
  }
};

export default function IndustryDetail() {
  const { id } = useParams();
  const data = DETAILS[id] || { title: 'Industry', lead: '', body: 'Details coming soon.' };
  const navigate = useNavigate();

  // map each industry to an Unsplash query (browser will fetch a relevant image)
  // include a stable `sig` per industry so Unsplash returns a distinct image per id
  const imageQueries = {
    'healthcare': 'healthcare medical hospital',
    'finance': 'finance banking fintech',
    'retail': 'retail shopping e-commerce',
    'manufacturing': 'manufacturing factory industry',
    'local-bar-associations': 'law legal courthouse',
    'digital-marketing': 'digital marketing advertising social-media'
  };
  const remoteImage = imageQueries[id]
    ? `https://source.unsplash.com/1200x400/?${encodeURIComponent(imageQueries[id])}&sig=${encodeURIComponent(id)}`
    : null;
  // Prefer local industry images if present. Try these candidates in order via the img error handler.
  // support alternate filenames (e.g. 'finance' might be 'financial.png')
  const aliasMap = { finance: 'financial', 'digital-marketing': 'digitalmarketing' };
  const alias = aliasMap[id];
  const localCandidates = [
    `/industries/${id}.jpg`,
    `/industries/${id}.png`,
    alias ? `/industries/${alias}.jpg` : null,
    alias ? `/industries/${alias}.png` : null,
    `/images/industries/${id}.jpg`,
    `/images/industries/${id}.png`,
    alias ? `/images/industries/${alias}.jpg` : null,
    alias ? `/images/industries/${alias}.png` : null,
    `/images/industries/${id}.svg`
  ].filter(Boolean);
  const bundledFallback = '/header-1.jpg';

  const [form, setForm] = React.useState({ name: '', email: '', company: '', message: '' });
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const onChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await fetch(`${API_BASE}/api/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, subject: `Industry inquiry: ${data.title}` }) });
      setSent(true);
      setForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Container sx={{ py: 6 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ width: '100%', height: { xs: 160, md: 260 }, borderRadius: 2, overflow: 'hidden', mb: 2 }}>
          <img
            loading="lazy"
            src={(localCandidates[0]) || remoteImage || bundledFallback}
            alt={`${data.title} hero`}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => {
              try {
                // iterate through localCandidates, then remoteImage, then bundledFallback
                const img = e.target;
                const tried = img.dataset.tried ? img.dataset.tried.split(',') : [];
                // build list of sources to try
                const sources = [...localCandidates, remoteImage, bundledFallback].filter(Boolean);
                const next = sources.find(s => !tried.includes(s));
                if (next) {
                  tried.push(next);
                  img.dataset.tried = tried.join(',');
                  img.src = next;
                } else {
                  img.onerror = null;
                }
              } catch (err) {
                e.target.onerror = null;
                e.target.src = bundledFallback;
              }
            }}
          />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{data.title}</Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>{data.lead}</Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Typography variant="body1" sx={{ mb: 3 }}>{data.body}</Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Selected case studies</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Project Alpha</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>A pilot that improved detection accuracy by 18% and reduced manual review time.</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Project Beta</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>An end-to-end pipeline that automated reporting and saved 120 hours per month.</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Talk to our experts</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Tell us about your project and we'll follow up to discuss scope and next steps.</Typography>

              <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField name="name" label="Name" size="small" value={form.name} onChange={onChange} required />
                <TextField name="email" label="Email" size="small" value={form.email} onChange={onChange} required />
                <TextField name="company" label="Company" size="small" value={form.company} onChange={onChange} />
                <TextField name="message" label="Short message" size="small" value={form.message} onChange={onChange} multiline rows={3} />
                <Button type="submit" variant="contained" disabled={sending} sx={{ mt: 1 }}>Request a consult</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Button
          onClick={() => {
            // navigate back to /industries without changing the URL hash, then smooth-scroll to the card
            navigate('/industries', { replace: true });
            setTimeout(() => {
              const el = document.getElementById(id);
              if (el) {
                // compute fixed header offset similar to Industries logic
                const fixedEls = Array.from(document.querySelectorAll('body *')).filter((n) => {
                  try {
                    const s = window.getComputedStyle(n);
                    return (s.position === 'fixed' || s.position === '-webkit-sticky') && parseFloat(s.top || '0') >= 0;
                  } catch (e) {
                    return false;
                  }
                });
                const offset = fixedEls.reduce((sum, n) => sum + (n.getBoundingClientRect().height || 0), 0);
                const top = el.getBoundingClientRect().top + window.pageYOffset - offset - 12;
                window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
              }
            }, 80);
          }}
          variant="outlined"
        >
          Back to Industries
        </Button>
      </Box>

      <Snackbar open={sent} autoHideDuration={4500} onClose={() => setSent(false)}>
        <Alert onClose={() => setSent(false)} severity="success" sx={{ width: '100%' }}>Thanks — we received your request and will reach out shortly.</Alert>
      </Snackbar>
    </Container>
  );
}
